import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";
import {
  Gift, Copy, Users, Trophy, Star, Sparkles, Check,
  ChevronRight, Loader2, Award, Share2,
} from "lucide-react";
import CollapsiblePanel from "./CollapsiblePanel";

const TIER_VISUAL = {
  tester: { color: "#64748B", bg: "#64748B15", icon: Star, labelBg: "Тестер", labelEn: "Tester" },
  idea_leader: { color: "#0EA5E9", bg: "#0EA5E915", icon: Sparkles, labelBg: "Идеен лидер", labelEn: "Idea Leader" },
  ambassador: { color: "#F59E0B", bg: "#F59E0B15", icon: Trophy, labelBg: "Посланик", labelEn: "Ambassador" },
  clinical_partner: { color: "#8B5CF6", bg: "#8B5CF615", icon: Award, labelBg: "Клиничен партньор", labelEn: "Clinical Partner" },
};

export default function ReferralPanel() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [myCode, setMyCode] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyCode, setApplyCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [codeRes, statsRes] = await Promise.all([
        axios.get(`${API}/referral/my-code`),
        axios.get(`${API}/referral/stats`),
      ]);
      setMyCode(codeRes.data);
      setStats(statsRes.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyCode = () => {
    if (!myCode?.code) return;
    const shareUrl = `${window.location.origin}/register?ref=${myCode.code}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      toast.success(bg ? "Линкът е копиран!" : "Link copied!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareJourney = async () => {
    try {
      const token = (localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token")) || "";
      const resp = await fetch(`${API}/share/journey-image?lang=${bg ? 'bg' : 'en'}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("fetch failed");
      const blob = await resp.blob();
      const file = new File([blob], "nutramilo-journey.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Nutramilo",
          text: bg ? "Моето здравословно пътуване с Nutramilo" : "My health journey with Nutramilo",
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "nutramilo-journey.png";
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success(bg ? "Изображението е изтеглено — споделете в Instagram!" : "Image downloaded — share to Instagram!");
      }
    } catch {
      toast.error(bg ? "Грешка при генериране" : "Generation error");
    }
  };

  const handleApply = async () => {
    if (!applyCode.trim()) return;
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/referral/apply-code`, { code: applyCode.trim() });
      toast.success(data.message_bg || data.message || (bg ? "Кодът е приложен!" : "Code applied!"));
      setApplyCode("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Error"));
    } finally { setApplying(false); }
  };

  const profile = stats?.profile;
  const referrals = stats?.referrals || [];
  const tiers = stats?.tiers || {};
  const tierVis = TIER_VISUAL[profile?.tier] || TIER_VISUAL.tester;
  const TierIcon = tierVis.icon;

  if (loading) {
    return (
      <CollapsiblePanel
        title={bg ? "Реферална програма" : "Referral Program"}
        icon={Gift}
        iconColor="#F59E0B"
        credits={0}
        defaultOpen={false}
        storageKey="referral-panel"
        testId="referral-panel"
      >
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      </CollapsiblePanel>
    );
  }

  return (
    <CollapsiblePanel
      title={bg ? "Реферална програма" : "Referral Program"}
      subtitle={bg ? "Поканете приятели — 50 бонус AI анализа при първото им плащане" : "Invite friends — 50 bonus AI analyses on their first payment"}
      icon={Gift}
      iconColor="#F59E0B"
      credits={0}
      defaultOpen={false}
      storageKey="referral-panel"
      testId="referral-panel"
    >
      <div className="space-y-4">
        {/* Current Tier */}
        {profile && (
          <div className={`flex items-center gap-3 p-3 rounded-xl ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-gray-50"}`} data-testid="referral-tier-card">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: tierVis.bg }}>
              <TierIcon className="w-6 h-6" style={{ color: tierVis.color }} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                  {bg ? tierVis.labelBg : tierVis.labelEn}
                </span>
                <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: tierVis.bg, color: tierVis.color }}>
                  {profile.tier.toUpperCase()}
                </Badge>
              </div>
              <p className={`text-[10px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                {bg ? tiers[profile.tier]?.description_bg : tiers[profile.tier]?.description_en}
              </p>
            </div>
          </div>
        )}

        {/* Stats */}
        {profile && (
          <div className="grid grid-cols-3 gap-2" data-testid="referral-stats">
            {[
              { label: bg ? "Реферали" : "Referrals", value: profile.total_referrals || 0, color: "#0EA5E9", icon: Users },
              { label: bg ? "Бонус анализи" : "Bonus analyses", value: profile.total_credits_earned || 0, color: "#10B981", icon: Sparkles },
              { label: bg ? "Активни" : "Active", value: profile.active_referrals || 0, color: "#F59E0B", icon: Star },
            ].map(s => (
              <div key={s.label} className={`p-2.5 rounded-lg text-center ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-gray-50"}`}>
                <s.icon className="w-4 h-4 mx-auto mb-1" style={{ color: s.color }} />
                <p className="text-lg font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className={`text-[9px] ${isDark ? "text-gray-600" : "text-gray-400"}`}>{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* How it works */}
        <div className={`p-3 rounded-xl text-[11px] ${isDark ? "bg-[#0EA5E9]/10 text-gray-300 border border-[#0EA5E9]/20" : "bg-sky-50 text-gray-600 border border-sky-100"}`} data-testid="referral-how-it-works">
          <p className="font-semibold mb-1">{bg ? "Как работи:" : "How it works:"}</p>
          <ol className="space-y-0.5 list-decimal list-inside">
            <li>{bg ? "Споделете вашия уникален линк с приятел" : "Share your unique link with a friend"}</li>
            <li>{bg ? "Те се регистрират и ползват безплатния период" : "They register and use the trial"}</li>
            <li>{bg ? "Когато закупят план → вие получавате 50 бонус AI анализа, те получават 30" : "When they buy a plan → you earn 50 bonus AI analyses, they earn 30"}</li>
          </ol>
        </div>

        {/* My Code + Share */}
        {myCode && (
          <div className={`rounded-xl p-3 ${isDark ? "bg-gradient-to-r from-[#F59E0B]/5 to-[#EF4444]/5 border border-[#F59E0B]/15" : "bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100"}`} data-testid="referral-share">
            <p className={`text-[10px] font-bold uppercase mb-2 ${isDark ? "text-[#F59E0B]" : "text-amber-600"}`}>
              {bg ? "Вашият реферален код" : "Your referral code"}
            </p>
            <div className="flex items-center gap-2">
              <div className={`flex-1 px-3 py-2 rounded-lg font-mono text-sm font-bold tracking-wider text-center ${isDark ? "bg-[hsl(223,47%,10%)] text-white" : "bg-white text-[#1A1A2E]"}`} data-testid="referral-code">
                {myCode.code}
              </div>
              <Button size="sm" onClick={copyCode} data-testid="copy-referral-btn"
                className={`h-9 px-3 rounded-lg ${copied ? "bg-[#10B981] hover:bg-[#10B981]" : "bg-[#F59E0B] hover:bg-[#D97706]"} text-white`}>
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
              <Button size="sm" onClick={shareJourney} data-testid="share-journey-btn"
                className="h-9 px-3 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] hover:opacity-90 text-white"
                title={bg ? "Споделете в Instagram / Facebook" : "Share to Instagram / Facebook"}>
                <Share2 className="w-4 h-4" />
              </Button>
            </div>
            <p className={`text-[10px] mt-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
              {bg ? "💡 Натиснете иконата за споделяне, за да генерирате Instagram Story с вашия Endo Score + QR код" : "💡 Tap the share icon to generate an Instagram Story with your Endo Score + QR code"}
            </p>
          </div>
        )}

        {/* Apply Code */}
        <div className={`rounded-xl p-3 ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-gray-50"}`} data-testid="referral-apply">
          <p className={`text-[10px] font-medium mb-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            {bg ? "Имате реферален код?" : "Have a referral code?"}
          </p>
          <div className="flex items-center gap-2">
            <Input value={applyCode} onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
              placeholder="NM-XXXXXX" data-testid="apply-code-input"
              className={`flex-1 h-9 text-sm font-mono tracking-wider ${isDark ? "bg-gray-800 border-gray-700" : "bg-white"}`} />
            <Button size="sm" onClick={handleApply} disabled={applying || !applyCode.trim()}
              data-testid="apply-code-btn"
              className="h-9 px-3 bg-[#6366F1] hover:bg-[#4F46E5] text-white rounded-lg text-xs">
              {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronRight className="w-3 h-3" />}
            </Button>
          </div>
        </div>

        {/* Recent Referrals */}
        {referrals.length > 0 && (
          <div data-testid="referral-history">
            <p className={`text-[10px] font-bold uppercase mb-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              {bg ? "Последни реферали" : "Recent referrals"}
            </p>
            <div className="space-y-1.5">
              {referrals.slice(0, 5).map((r, i) => (
                <div key={`ref-${i}`} className={`flex items-center justify-between px-2.5 py-2 rounded-lg ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-white"}`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${isDark ? "bg-[#0EA5E9]/15 text-[#0EA5E9]" : "bg-sky-100 text-sky-600"}`}>
                      {(r.referred_name || "?").charAt(0).toUpperCase()}
                    </div>
                    <span className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {r.referred_name || r.referred_email || "User"}
                    </span>
                  </div>
                  <Badge className={`text-[9px] px-1.5 py-0 ${r.status === "active" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                    {r.status === "active" ? (bg ? "активен" : "active") : (bg ? "изчакващ" : "pending")}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </CollapsiblePanel>
  );
}
